import {syncable} from "../model/index.js";

/**
 * Loads an object into a map without always overriding the object pointer, thus making reactions trigger only when the
 * appropriate data changes.
 *
 * For example, a project selection dropdown which only uses the projects' names and UUIDs doesn't need to re-render
 * when one of the project's descriptions is changed. If we replace the entire project object (changing the pointer to
 * the object internally), the reaction would always trigger.
 */
export function loadObject(map: Record<string, Record<string, any>>, key: string, object: Record<string, any>): void {
	function overwriteArray(source: any[], target: any[]): any[] {
		if (source.length !== target.length)
			return target;

		for (let i = 0; i < target.length; i++)
			source[i] = overwrite(source[i], target[i]);

		return source;
	}

	function overwriteObject(source: Record<string, any>, target: Record<string, any>): Record<string, any> {
		// Delete appropriate props
		Object.keys(source)
			.filter(p => target[p] === undefined)
			.forEach(p => delete source[p]);

		// Overwrite existing props and add new ones, if they're not arrays or objects
		// eslint-disable-next-line no-return-assign
		Object.keys(target).forEach(prop => source[prop] = overwrite(source[prop], target[prop]));

		return source;
	}

	function overwrite(source: any, target: any): any {
		if (Array.isArray(source) && Array.isArray(target))
			return overwriteArray(source, target);
		else if (isLiteralObject(source) && isLiteralObject(target))
			return overwriteObject(source, target);
		else {
			// Don't do the recursive merging if we're dealing with functions, arrays, nulls, etc.
			return target;
		}
	}

	map[key] = overwrite(map[key], object);
}

/**
 * Returns true if the argument is a simple object.
 *
 * Returns false for:
 * - Class instances (e.g. a Date object)
 * - Arrays
 * - Null
 *
 * Thanks: https://stackoverflow.com/a/16608074/368864
 */
function isLiteralObject(a: any): boolean {
	return (!!a) && (a.constructor === Object);
}

/**
 * Returns a shallow copy of the object without the `syncable` properties.
 */
export function cleanUp(object: syncable): Record<string, any> {
	const forbidden = ["_sync"];

	return Object.keys(object).reduce(
		// @ts-ignore - Just trust me.
		(acc, cur) => Object.assign(acc, forbidden.includes(cur) ? {} : {[cur]: object[cur]}), {}
	);
}

export function prepareForCreate(object: syncable): syncable {
	return Object.assign({}, cleanUp(object) as syncable, {
		_sync: "creating"
	});
}

export function prepareForUpdate(object: syncable): syncable {
	return Object.assign({}, cleanUp(object) as syncable, {
		_sync: "updating"
	});
}
